import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
import { warmGalleryImages } from './product-gallery-warm.js';

/**
 * How long the current image stays faded out before the slide is swapped underneath.
 * Pairs with the opacity transition in snippets/product-media.liquid.
 */
const CROSSFADE_HOLD_MS = 180;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    // Fired synchronously on click, well before the picker's fetch resolves. This is
    // what makes the colour swap feel instant.
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    // zoom-dialog.js closes by dispatching this on window, not on itself.
    window.addEventListener(DialogCloseEvent.eventName, this.#applyZoomColour, { signal });

    warmGalleryImages(this);

    // Show only the selected colour's photo(s) from the start. Read the server-rendered
    // markup rather than the slideshow's refs, which may not be upgraded yet here.
    const initial = this.#slideElements().find((slide) => slide.getAttribute('aria-hidden') !== 'true');
    const initialId = initial?.getAttribute('slide-id');
    if (initialId) this.#showOnlyColour(initialId);
  }

  #controller = new AbortController();

  /** @type {number | undefined} */
  #crossfadeTimer;

  /** @type {number | undefined} */
  #revealTimer;

  /** @type {Record<string, number> | null} */
  #variantMediaMap = null;

  /** Media id the shopper last brought up in the zoom dialog, applied when it closes. */
  #pendingZoomColour = /** @type {string | null} */ (null);

  disconnectedCallback() {
    super.disconnectedCallback();

    window.clearTimeout(this.#crossfadeTimer);
    window.clearTimeout(this.#revealTimer);
    this.#controller.abort();
  }

  /**
   * option-value-id -> media-id, emitted by snippets/tgc-variant-media-map.liquid.
   * Only contains option values whose variants all share one image, which in practice
   * means colour and never size.
   *
   * @returns {Record<string, number>}
   */
  get variantMediaMap() {
    if (this.#variantMediaMap) return this.#variantMediaMap;

    // Same target as the listeners in connectedCallback. The quick-add modal renders
    // the product-information grid inside a <dialog> appended to <body>, and the map
    // ships with it. A '.shopify-section'-only lookup therefore finds nothing, so every
    // colour tap fell back to refetching the whole product page before the image moved.
    // Measured 2026-09-07 on an unthrottled connection: 5-34s from the catalogue,
    // 45-60s from a product page.
    const source = this.closest('.shopify-section, dialog')?.querySelector('[data-tgc-variant-media-map]');

    try {
      this.#variantMediaMap = source?.textContent ? JSON.parse(source.textContent) : {};
    } catch (error) {
      // A malformed map must not break the gallery; the variant:update path still works.
      this.#variantMediaMap = {};
    }

    return this.#variantMediaMap ?? {};
  }

  /**
   * Handles a swatch being clicked, before the server has confirmed anything.
   *
   * @param {Event & { detail?: { resource?: { id?: string } } }} event
   */
  #handleVariantSelected = (event) => {
    const optionValueId = event.detail?.resource?.id;

    if (!optionValueId || !this.slideshow) return;

    // Combined listings point an option at a different product entirely. That genuinely
    // needs the server response, so leave those to the variant:update path.
    const option = this.closest('.shopify-section')?.querySelector(
      `[data-option-value-id="${CSS.escape(String(optionValueId))}"]`
    );
    if (option instanceof HTMLElement && option.dataset.connectedProductUrl) return;

    const mediaId = this.variantMediaMap[String(optionValueId)];
    if (!mediaId) return;

    this.#selectMedia(mediaId);
  };

  /**
   * Handles a variant update event by selecting the variant's media in slideshow.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const variantResource = event.detail.resource;

    if (!variantResource || !this.slideshow) return;

    const featuredMediaId = variantResource.featured_media?.id;

    if (!featuredMediaId) return;

    this.#selectMedia(featuredMediaId);
  };

  /**
   * Crossfades the gallery to a media id.
   *
   * Skips entirely when that media is already on screen. This guard predates the
   * optimistic swap and is what stops the two paths animating the same change twice:
   * once #handleVariantSelected has landed, the later variant:update is a no-op. It
   * also covers the picker auto-selecting a default size on page load, which fires an
   * update even though no image changed.
   *
   * @param {number | string} featuredMediaId
   */
  #selectMedia(featuredMediaId) {
    if (!this.slideshow) return;

    // slideshow.current is an index into slideshow.slides, which skips [hidden] slides -
    // NOT into refs.slides, which is every slide. The two matched until #showOnlyColour
    // started hiding the other colours; after that refs.slides[current] was always the
    // first colour's photo (owner 2026-09-17). Two bugs fell out of that one mismatch:
    // picking the first colour again always matched here and bailed out, so that colour
    // would not load until the page was reloaded, and every other colour lost the
    // no-op guard below, so the later variant:update crossfaded a second time.
    const activeSlide = this.slideshow.slides?.[this.slideshow.current];
    if (activeSlide?.getAttribute('slide-id') === String(featuredMediaId)) return;

    // Grid presentation shows every image at once, so it swaps instantly.
    if (this.presentation !== 'carousel') {
      this.#showOnlyColour(featuredMediaId);
      this.slideshow.select({ id: featuredMediaId }, undefined, { animate: false });
      return;
    }

    // Tapping a second colour mid-fade must not leave data-media-updating stuck on,
    // and must not let an older timer select the wrong slide afterwards.
    window.clearTimeout(this.#crossfadeTimer);
    window.clearTimeout(this.#revealTimer);

    this.setAttribute('data-media-updating', '');
    this.#crossfadeTimer = window.setTimeout(() => {
      this.#showOnlyColour(featuredMediaId);
      this.slideshow?.select({ id: featuredMediaId }, undefined, { animate: false });
      this.#revealAfterSwap();
    }, CROSSFADE_HOLD_MS);
  }

  /**
   * Owner 2026-09-15: shoppers should not be able to swipe from one colour's photo to
   * another; the colour changes only through the swatches. Theme setting hide_variants
   * cannot do this here (its Liquid filter is disabled so slide ids stay stable for the
   * instant swap), so every slide stays in the DOM, eager and warm, and the slides and
   * thumbnails of the OTHER colours are simply hidden. Slideshow skips [hidden] slides.
   * Photos not tied to a colour stay visible. No-op unless the media id is a colour photo
   * and the product has at least two colour photos.
   *
   * @param {number | string} mediaId
   */
  #showOnlyColour(mediaId) {
    const slides = this.#slideElements();
    if (!slides.length) return;

    const colourMediaIds = new Set(Object.values(this.variantMediaMap).map(String));
    const keep = String(mediaId);
    if (colourMediaIds.size < 2 || !colourMediaIds.has(keep)) return;

    // Thumbnails are rendered in slide order and select by index.
    const thumbnails = [...this.querySelectorAll('.slideshow-controls__thumbnail')].filter(
      (thumbnail) => !thumbnail.closest('zoom-dialog')
    );
    let visible = 0;
    slides.forEach((slide, index) => {
      const id = slide.getAttribute('slide-id');
      const hide = id !== null && colourMediaIds.has(id) && id !== keep;
      slide.hidden = hide;
      // Slideshow.select() marks a hidden slide it was asked for with [reveal], and
      // base.css only collapses `[hidden]:not([reveal])`. A slide left carrying both
      // keeps its box, so the row briefly holds two slides, the scroller gains something
      // to scroll and the new photo lands offset before snapping back - the "renders
      // ~20px to the left, then slides into the middle" the owner saw on a phone.
      if (hide) slide.removeAttribute('reveal');
      // base.css hides any slide that is not aria-hidden="false" with
      // `content-visibility: hidden`, which on a phone collapses it to 0x0 - so simply
      // clearing [hidden] left the newly chosen colour present but invisible, and the
      // gallery went blank on every colour tap (owner 2026-09-21, mobile only; desktop
      // sizes the slide from the flex row and hid the fault). Slideshow.select() would
      // normally set this, but it returns early here: once the other colours are hidden
      // the wanted slide is already index 0, so its requested index equals `current`.
      slide.setAttribute('aria-hidden', String(hide));
      if (!hide) visible++;
      const thumbnail = thumbnails[index];
      if (thumbnail instanceof HTMLElement) thumbnail.hidden = hide;
    });

    // One photo left: the arrows, "1 / 8" counter and thumbnail strip only mislead.
    // Styled in layout/theme.liquid.
    this.toggleAttribute('data-tgc-single-slide', visible === 1);

    // With one slide on show there is nothing to scroll to, so any leftover offset from
    // the outgoing colour is just a visible jog. Belt to the braces above.
    if (visible === 1) {
      const scroller = this.slideshow?.querySelector('slideshow-slides');
      if (scroller instanceof HTMLElement && scroller.scrollLeft !== 0) scroller.scrollLeft = 0;
    }
  }

  /** The main gallery's slides (not the zoom dialog's copies), in DOM order. */
  #slideElements() {
    const slideshow = this.slideshow ?? this.querySelector('slideshow-component');
    if (!slideshow) return [];
    return [...slideshow.querySelectorAll('slideshow-slide[slide-id]')].filter((slide) => !slide.closest('zoom-dialog'));
  }

  /**
   * Fades the gallery back in once the swapped slide has had a frame to paint.
   *
   * The rAF is what makes it look right, but rAF does NOT fire in a backgrounded
   * tab. Relying on it alone means switching tabs mid-swap leaves the attribute set
   * and the active image stuck at opacity 0, so you return to a blank gallery. The
   * timer is the guarantee; browsers throttle it in the background but it always
   * fires. Whichever wins, the reveal happens exactly once.
   */
  #revealAfterSwap() {
    let revealed = false;

    const reveal = () => {
      if (revealed) return;
      revealed = true;
      window.clearTimeout(this.#revealTimer);
      this.removeAttribute('data-media-updating');
    };

    requestAnimationFrame(reveal);
    this.#revealTimer = window.setTimeout(reveal, 100);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    const mediaId = this.#zoomMediaIdAt(event.detail.index);
    if (!mediaId) return;

    // Owner 2026-09-21: the zoom view lists every colour and lets the shopper swipe
    // through them, so browsing it has to move the real selection - otherwise they close
    // the zoom looking at Maroon with Navy still ticked and add the wrong colour. The
    // swatch is clicked when the dialog CLOSES, not on every swipe: this fires on a 50ms
    // scroll debounce, and each swatch click costs a section fetch, so applying it live
    // would fire one per colour scrolled past and re-render the section under an open
    // dialog.
    if (this.#colourOptionFor(mediaId)) {
      this.#pendingZoomColour = mediaId;
      return;
    }

    // Not a colour photo, so nothing to select: just move the gallery under the dialog.
    // By id, not by the event's index - that indexes every media, while slideshow.select
    // counts only the slides still visible after #showOnlyColour.
    this.slideshow?.select({ id: mediaId }, undefined, { animate: false });
  };

  /** Media id the zoom dialog is showing at `index`, from its `product-<id>-<n>` li. */
  #zoomMediaIdAt(index) {
    const media = this.refs.zoomDialogComponent?.refs.media?.[index];
    return media?.id?.match(/^product-(\d+)-/)?.[1] ?? null;
  }

  /** The option value id whose colour photo is `mediaId`, if it is one. */
  #colourOptionFor(mediaId) {
    const map = this.variantMediaMap;
    return Object.keys(map).find((optionValueId) => String(map[optionValueId]) === String(mediaId)) ?? null;
  }

  /**
   * Ticks the colour the shopper left the zoom dialog on. Clicking the swatch reuses the
   * theme's own variant path, so price, availability, the form's variant id and this
   * gallery all follow, exactly as if they had tapped the swatch themselves.
   */
  #applyZoomColour = () => {
    const mediaId = this.#pendingZoomColour;
    this.#pendingZoomColour = null;
    if (!mediaId) return;

    const optionValueId = this.#colourOptionFor(mediaId);
    if (!optionValueId) return;

    const swatch = this.closest('.shopify-section, dialog')?.querySelector(
      `input[type="radio"][data-option-value-id="${CSS.escape(optionValueId)}"]`
    );
    if (swatch instanceof HTMLInputElement && !swatch.checked) swatch.click();
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
